import type {
  AccountProfile,
  AccountStats,
  AuthSuccess,
  AvatarId,
  CardTheme,
  GameHistoryEntry,
  PublicProfile,
  PublicServerConfig,
} from "../types/protocol";

/** Vide en production : l'API vit sur la même origine, le proxy Vite s'en charge en développement. */
const baseUrl = import.meta.env.VITE_SERVER_URL?.trim() || "";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    if (details !== undefined) this.details = details;
  }
}

export interface ApiRequestInit extends Omit<RequestInit, "body"> {
  /** Corps sérialisé en JSON ; rejouable tel quel après un rafraîchissement. */
  json?: unknown;
  /** Corps binaire brut ; un `Blob` se rejoue, contrairement à un flux. */
  blob?: Blob;
  /** Coupe la tentative de rafraîchissement, pour les routes d'authentification elles-mêmes. */
  retryOnExpired?: boolean;
}

export interface RegisterInput {
  email: string;
  password: string;
  displayName: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface ProfilePatch {
  displayName?: string;
  avatarId?: AvatarId;
  cardTheme?: CardTheme;
}

export interface PasswordChangeInput {
  currentPassword: string;
  newPassword: string;
}

export type AccessTokenListener = (token: string | null) => void;

// Le jeton d'accès reste en mémoire : seul le jeton de rafraîchissement persiste,
// dans un cookie HttpOnly que le JavaScript ne peut pas lire.
let accessToken: string | null = null;
const tokenListeners = new Set<AccessTokenListener>();

export const getAccessToken = (): string | null => accessToken;

export function setAccessToken(token: string | null): void {
  if (accessToken === token) return;
  accessToken = token;
  for (const listener of [...tokenListeners]) listener(token);
}

/** Permet à la couche socket de se réidentifier dès que le jeton change. */
export function onAccessTokenChange(listener: AccessTokenListener): () => void {
  tokenListeners.add(listener);
  return () => {
    tokenListeners.delete(listener);
  };
}

const networkError = () =>
  new ApiError("NETWORK_ERROR", "Le serveur est injoignable. Vérifie ta connexion.", 0);

const statusMessage = (status: number) =>
  status >= 500
    ? "Le serveur ne répond pas correctement. Réessaie dans un instant."
    : "La requête a été refusée.";

const readJson = async (response: Response): Promise<unknown> => {
  // Un intermédiaire (nginx, service worker) peut répondre en HTML : on ne casse pas dessus.
  try {
    const text = await response.text();
    return text ? (JSON.parse(text) as unknown) : null;
  } catch {
    return null;
  }
};

const toApiError = async (response: Response): Promise<ApiError> => {
  const payload = await readJson(response);
  const envelope =
    payload && typeof payload === "object" ? (payload as { error?: unknown }).error : null;
  if (envelope && typeof envelope === "object") {
    const { code, message, details } = envelope as {
      code?: unknown;
      message?: unknown;
      details?: unknown;
    };
    if (typeof code === "string" && typeof message === "string") {
      return new ApiError(code, message, response.status, details);
    }
  }
  return new ApiError(`HTTP_${response.status}`, statusMessage(response.status), response.status);
};

const parse = async <T,>(response: Response): Promise<T> => {
  if (response.status === 204) return undefined as T;
  return (await readJson(response)) as T;
};

const send = async (path: string, init: ApiRequestInit): Promise<Response> => {
  const { json, blob, retryOnExpired, headers, ...rest } = init;
  const merged = new Headers(headers);
  merged.set("Accept", "application/json");
  if (json !== undefined) merged.set("Content-Type", "application/json");
  // Le serveur tranche sur les octets, mais il n'accepte de lire le corps que
  // si le type annoncé fait partie de sa liste.
  if (blob !== undefined) merged.set("Content-Type", blob.type || "image/webp");
  if (accessToken) merged.set("Authorization", `Bearer ${accessToken}`);

  const body = json === undefined ? blob : JSON.stringify(json);
  try {
    return await fetch(`${baseUrl}/api${path}`, {
      ...rest,
      credentials: "include",
      headers: merged,
      ...(body === undefined ? {} : { body }),
    });
  } catch {
    throw networkError();
  }
};

let refreshInFlight: Promise<AuthSuccess> | null = null;

// Les appels concurrents partagent une seule requête pour éviter la tempête de
// rafraîchissements, et pour ne pas invalider le cookie fraîchement pivoté.
const runRefresh = (): Promise<AuthSuccess> => {
  refreshInFlight ??= (async () => {
    const response = await send("/auth/refresh", { method: "POST" });
    if (!response.ok) throw await toApiError(response);
    return parse<AuthSuccess>(response);
  })()
    .then((session) => {
      setAccessToken(session.accessToken);
      return session;
    })
    .catch((error: unknown) => {
      setAccessToken(null);
      throw error;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const response = await send(path, init);
  if (response.ok) return parse<T>(response);

  const error = await toApiError(response);
  const expired =
    init.retryOnExpired !== false && response.status === 401 && error.code === "UNAUTHENTICATED";
  if (!expired) throw error;

  try {
    await runRefresh();
  } catch {
    // Le rafraîchissement a échoué : l'erreur d'origine reste la plus parlante.
    throw error;
  }

  const replayed = await send(path, { ...init, retryOnExpired: false });
  if (replayed.ok) return parse<T>(replayed);
  throw await toApiError(replayed);
}

const authenticate = async (path: string, json: unknown): Promise<AuthSuccess> => {
  const session = await apiFetch<AuthSuccess>(path, {
    method: "POST",
    json,
    retryOnExpired: false,
  });
  setAccessToken(session.accessToken);
  return session;
};

export const getPublicConfig = (): Promise<PublicServerConfig> =>
  apiFetch<PublicServerConfig>("/config", { retryOnExpired: false });

export const register = (input: RegisterInput): Promise<AuthSuccess> =>
  authenticate("/auth/register", input);

export const login = (input: LoginInput): Promise<AuthSuccess> => authenticate("/auth/login", input);

export const refreshSession = (): Promise<AuthSuccess> => runRefresh();

export const logout = async (): Promise<void> => {
  try {
    await apiFetch<void>("/auth/logout", { method: "POST", retryOnExpired: false });
  } finally {
    setAccessToken(null);
  }
};

export const getMe = async (): Promise<AccountProfile> =>
  (await apiFetch<{ user: AccountProfile }>("/me")).user;

export const updateProfile = async (patch: ProfilePatch): Promise<AccountProfile> =>
  (await apiFetch<{ user: AccountProfile }>("/me", { method: "PATCH", json: patch })).user;

/**
 * URL immuable de la photo : l'empreinte fait partie du chemin, si bien qu'une
 * nouvelle photo est une nouvelle ressource et jamais une révalidation.
 */
export const avatarPhotoUrl = (profile: PublicProfile): string | null =>
  profile.avatarPhoto === undefined
    ? null
    : `${baseUrl}/api/avatars/${profile.userId}/${profile.avatarPhoto}.webp`;

export const uploadAvatarPhoto = async (photo: Blob): Promise<AccountProfile> =>
  (await apiFetch<{ user: AccountProfile }>("/me/avatar", { method: "PUT", blob: photo })).user;

export const removeAvatarPhoto = async (): Promise<AccountProfile> =>
  (await apiFetch<{ user: AccountProfile }>("/me/avatar", { method: "DELETE" })).user;

export const changePassword = (input: PasswordChangeInput): Promise<void> =>
  apiFetch<void>("/me/password", { method: "POST", json: input });

export const deleteAccount = async (password: string): Promise<void> => {
  await apiFetch<void>("/me", { method: "DELETE", json: { password } });
  setAccessToken(null);
};

export const getStats = async (): Promise<AccountStats> =>
  (await apiFetch<{ stats: AccountStats }>("/me/stats")).stats;

export const getGameHistory = async (limit?: number): Promise<GameHistoryEntry[]> => {
  const query = limit === undefined ? "" : `?limit=${Math.min(50, Math.max(1, Math.trunc(limit)))}`;
  return (await apiFetch<{ games: GameHistoryEntry[] }>(`/me/games${query}`)).games;
};
